import React, { useState, useEffect, useRef } from 'react';
import { alertsAPI } from '../services/api';
import './Troubleshoot.css';

const Troubleshoot = ({ alertId, podName, nodeName, alert, onAnalysisDone }) => {
  const initialDiag = alert?._live_diagnostics || null;
  const [loading, setLoading]             = useState(false);
  const [loadingPhase, setLoadingPhase]   = useState('baseline');
  const [diagnostics, setDiagnostics]     = useState(initialDiag);
  const [error, setError]                 = useState(null);
  const [activeTab, setActiveTab]         = useState(() => {
    // Pick best initial tab from existing diagnostics
    if (!initialDiag) return 'logs';
    if (initialDiag.os_diagnostics)      return 'os';
    if (initialDiag.node_top_raw || initialDiag.pods_on_node || initialDiag.top_pods_by_disk) return 'node';
    if (initialDiag.logs?.length > 0)    return 'logs';
    if (initialDiag.describe)            return 'describe';
    if (initialDiag.node)                return 'node';
    return 'logs';
  });
  const [analyzing, setAnalyzing]         = useState(false);
  const [analysisError, setAnalysisError] = useState(null);
  const autoFetchedRef = useRef(false);

  const runDiagnostics = async () => {
    setLoading(true);
    setLoadingPhase('baseline');
    setError(null);
    setDiagnostics(null);
    setAnalysisError(null);
    // 45s timeout — Pass 6 searches across clusters, each with 5s timeout
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 45000);
    // After 8s switch to "collecting additional diagnostics" message (Phase 2+3)
    const phaseTimer = setTimeout(() => setLoadingPhase('planning'), 8000);
    try {
      const res = await alertsAPI.troubleshoot(alertId, { signal: controller.signal });
      clearTimeout(timer);
      clearTimeout(phaseTimer);
      const diag = res.data.diagnostics;
      setDiagnostics(diag);
      // Auto-select most useful tab
      const pod = diag.pod;
      const podIsPending = pod && !pod.error && (
        pod.phase === 'Pending' ||
        pod.containers?.some(c => c.state === 'waiting')
      );
      if (diag.info)                                                          setActiveTab('info');
      else if (diag.os_diagnostics)                                           setActiveTab('os');
      else if (diag.node_top_raw || diag.pods_on_node || diag.top_pods_by_cpu || diag.top_pods_by_disk)  setActiveTab('node');
      else if (diag.logs?.length > 0 && !podIsPending)                       setActiveTab('logs');
      else if (diag.describe)                                                 setActiveTab('describe');
      else if (diag.events?.length > 0)                                      setActiveTab('events');
      else if (diag.logs?.length > 0)                                        setActiveTab('logs');
      else if (diag.pod)                                                      setActiveTab('pod');
      else                                                                    setActiveTab('node');
    } catch (e) {
      clearTimeout(timer);
      clearTimeout(phaseTimer);
      if (e.name === 'AbortError' || e.code === 'ERR_CANCELED') {
        setError('Diagnostics timed out (45s). The cluster may be unreachable or slow. Try again.');
      } else {
        setError(e.response?.data?.detail || 'Failed to run diagnostics');
      }
    } finally {
      setLoading(false);
    }
  };

  // Auto-fetch on first open when no cached diagnostics exist
  useEffect(() => {
    if (!initialDiag && !autoFetchedRef.current) {
      autoFetchedRef.current = true;
      runDiagnostics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const analyzeWithAI = async () => {
    setAnalyzing(true);
    setAnalysisError(null);
    try {
      await alertsAPI.triggerAnalysis(alertId, true);
      let attempts = 0;
      const poll = async () => {
        const res = await alertsAPI.getAnalysisStatus(alertId);
        const status = res.data.status;
        if (status === 'completed') {
          setAnalyzing(false);
          if (onAnalysisDone) onAnalysisDone();
        } else if (status === 'failed') {
          setAnalysisError('AI analysis failed. Try again.');
          setAnalyzing(false);
        } else if (attempts < 80) {
          attempts++;
          setTimeout(poll, attempts < 20 ? 2000 : 3000);
        } else {
          setAnalysisError('Analysis timed out. Check the AI Analysis tab — it may have completed.');
          setAnalyzing(false);
        }
      };
      poll();
    } catch (e) {
      setAnalysisError(e.response?.data?.detail || 'Failed to trigger AI analysis');
      setAnalyzing(false);
    }
  };

  const hasLogs        = diagnostics?.logs?.length > 0;
  const hasDescribe    = !!diagnostics?.describe;
  const hasPod         = diagnostics?.pod && !diagnostics.pod.error;
  const hasNode        = (diagnostics?.node && !diagnostics.node.error)
                         || diagnostics?.node_top_raw?.length > 0
                         || diagnostics?.pods_on_node?.length > 0
                         || diagnostics?.top_pods_by_cpu?.length > 0
                         || diagnostics?.top_pods_by_disk?.length > 0;
  const hasEvents      = diagnostics?.events?.length > 0;
  const hasNodeTop     = diagnostics?.node_top?.length > 0 || diagnostics?.node_top_raw?.length > 0;
  const hasPodTop      = diagnostics?.pod_top?.length > 0;
  const hasOsDiag      = diagnostics?.os_diagnostics && !diagnostics.os_diagnostics.error;
  const hasInfo        = !!diagnostics?.info;

  const getNoLogReason = () => {
    const pod = diagnostics?.pod;
    if (!pod || pod.error) return null;
    const phase = pod.phase;
    const containers = pod.containers || [];
    const waiting = containers.find(c => c.state === 'waiting');
    if (waiting?.reason === 'ImagePullBackOff' || waiting?.reason === 'ErrImagePull') {
      return `No logs — pod cannot start (${waiting.reason}: image pull failed). Check Events tab.`;
    }
    if (waiting?.reason === 'CrashLoopBackOff') {
      return 'Container is crash-looping. Previous container logs may appear above if available.';
    }
    if (phase === 'Pending') return 'Pod is Pending — container has not started yet. No logs available.';
    if (phase === 'Succeeded' || phase === 'Failed') return `Pod is ${phase} — logs may have been cleaned up.`;
    return 'No logs captured from this container.';
  };

  const renderLogs = () => {
    if (!hasLogs) {
      const reason = getNoLogReason();
      return (
        <div style={{ padding: '12px 0' }}>
          <p className="ts-empty" style={{ marginBottom: reason ? 8 : 0 }}>No container logs available</p>
          {reason && (
            <div className="banner warning">
              {reason}
            </div>
          )}
          {hasDescribe ? (
            <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--brand-strong)', marginTop: 8 }}>
              <strong>kubectl describe pod</strong> output is available — check the <strong>Describe</strong> tab for eviction reason and node conditions.
              AI analysis will use the describe output as its primary evidence.
            </div>
          ) : (
            <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
              AI can still analyze this alert using pod state and events below.
            </p>
          )}
        </div>
      );
    }
    return <pre className="ts-logs">{diagnostics.logs.join('\n')}</pre>;
  };

  const renderDescribe = () => {
    if (!hasDescribe) return <p className="ts-empty">No describe output available</p>;
    return (
      <div>
        <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--brand-strong)', marginBottom: 8 }}>
          Raw <code>kubectl describe pod</code> output — useful for evicted, pending, or OOMKilled pods where logs are unavailable.
        </div>
        <pre className="ts-logs" style={{ fontSize: 11 }}>{diagnostics.describe}</pre>
      </div>
    );
  };

  const renderPod = () => {
    const pod = diagnostics?.pod;
    if (!pod) return <p className="ts-empty">No pod data</p>;
    if (pod.error) return <p className="ts-error">{pod.error}</p>;
    // Prominent READY summary for "pod not running" alerts (e.g. 0/1 on a node).
    const ready = pod.ready;                        // e.g. "0/1"
    const notReady = ready && /^0\//.test(String(ready).trim());
    return (
      <div className="ts-section">
        {ready && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12,
            padding: '10px 14px', borderRadius: 8,
            background: notReady ? 'var(--color-warning-50)' : 'var(--color-success-50)',
            border: `1px solid ${notReady ? 'var(--color-warning-500)' : 'var(--color-success-500)'}`,
          }}>
            <span style={{ fontSize: 20, fontWeight: 700, color: notReady ? 'var(--color-warning-700)' : 'var(--color-success-700)' }}>
              {ready} READY
            </span>
            <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {notReady ? `Pod is NOT running` : 'Pod is running'}
              {pod.node ? ` on node ${pod.node}` : ''}
              {pod.status && pod.status !== pod.phase ? ` — ${pod.status}` : ''}
              {typeof pod.restarts === 'number' ? ` · ↺ ${pod.restarts} restarts` : ''}
            </span>
          </div>
        )}
        <div className="ts-meta-grid">
          <div><span className="ts-label">Name</span><span className="ts-val">{pod.name}</span></div>
          <div><span className="ts-label">Namespace</span><span className="ts-val">{pod.namespace}</span></div>
          <div><span className="ts-label">Phase</span><span className={`ts-badge phase-${pod.phase?.toLowerCase()}`}>{pod.phase}</span></div>
          <div><span className="ts-label">Node</span><span className="ts-val">{pod.node}</span></div>
        </div>
        {diagnostics?.workload && diagnostics?.workload_pods?.length > 0 && (
          <>
            <h4>{diagnostics.workload.kind} {diagnostics.workload.name} — pods</h4>
            <table className="ts-top-table">
              <thead><tr><th>Pod</th><th>Ready</th><th>Status</th><th>Restarts</th><th>Node</th></tr></thead>
              <tbody>
                {diagnostics.workload_pods.map((p, i) => {
                  const bad = p.ready && /^0\//.test(String(p.ready)) || (p.ready && p.ready.split('/')[0] !== p.ready.split('/')[1]);
                  return (
                    <tr key={i} className={bad ? 'ts-top-row-hot' : ''}>
                      <td className="ts-top-pod">{p.name}</td>
                      <td style={{ fontWeight: bad ? 700 : 400, color: bad ? 'var(--color-warning-700)' : undefined }}>{p.ready}</td>
                      <td>{p.status}</td><td>{p.restarts}</td><td>{p.node}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </>
        )}
        {pod.containers?.length > 0 && (
          <>
            <h4>Containers</h4>
            {pod.containers.map((c, i) => (
              <div key={i} className={`ts-container ${c.state === 'waiting' ? 'ts-container-warn' : ''}`}>
                <div className="ts-container-header">
                  <strong>{c.name}</strong>
                  <span className={`ts-badge state-${c.state}`}>{c.state}</span>
                  <span className="ts-restarts">↺ {c.restart_count} restarts</span>
                </div>
                {c.reason && <div className="ts-reason">Reason: <strong>{c.reason}</strong>{c.message ? ` — ${c.message}` : ''}</div>}
                {c.exit_code !== undefined && <div className="ts-reason">Exit code: <strong>{c.exit_code}</strong></div>}
              </div>
            ))}
          </>
        )}
        {pod.conditions?.length > 0 && (
          <>
            <h4>Conditions</h4>
            <div className="ts-conditions">
              {pod.conditions.map((c, i) => (
                <div key={i} className={`ts-condition ${c.status === 'False' ? 'ts-condition-bad' : ''}`}>
                  <span className="ts-label">{c.type}</span>
                  <span className={`ts-badge status-${c.status?.toLowerCase()}`}>{c.status}</span>
                  {c.reason && <span className="ts-reason-inline">{c.reason}</span>}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    );
  };

  // Reusable: Top Pods by Memory table + CPU/mem process drill-down. Rendered in
  // both renderNode paths so memory/CPU alerts get the same depth as disk.
  const renderCpuMemExtras = () => (
    <>
      {diagnostics?.top_pods_by_memory?.length > 0 && diagnostics?.primary_resource === 'memory' && (
        <>
          <h4 style={{ marginTop: 16 }}>Top Pods by Memory (kubectl top pods --sort-by=memory)</h4>
          <pre className="ts-logs" style={{ fontSize: 11 }}>{diagnostics.top_pods_by_memory.join('\n')}</pre>
        </>
      )}
      {diagnostics?.proc_drilldown?.length > 0 && (
        <>
          <h4 style={{ marginTop: 16 }}>Process breakdown inside top pods (ps)</h4>
          <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--brand-strong)', marginBottom: 8 }}>
            Processes inside each top consumer, sorted by {diagnostics?.primary_resource === 'memory' ? 'memory (RSS)' : 'CPU'} — the exact process driving usage.
          </div>
          {diagnostics.proc_drilldown.map((p, i) => (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{p.namespace}/{p.pod}</div>
              <pre className="ts-logs" style={{ fontSize: 11 }}>{(p.processes || []).join('\n')}</pre>
            </div>
          ))}
        </>
      )}
    </>
  );

  const renderNode = () => {
    const node = diagnostics?.node;
    const nodeTop      = diagnostics?.node_top_raw;
    const podsOnNode   = diagnostics?.pods_on_node;
    const topPodsCpu   = diagnostics?.top_pods_by_cpu;

    // If no node struct but we have resource data, still render resource section
    const hasNodeStruct = node && !node.error;
    const hasResourceData = nodeTop?.length > 0 || podsOnNode?.length > 0 || topPodsCpu?.length > 0;

    if (!node && !hasResourceData) return <p className="ts-empty">No node data</p>;

    if (node?.error && !hasResourceData) {
      const errStr = typeof node.error === 'string' ? node.error : JSON.stringify(node.error);
      const is404  = errStr.includes('404') || errStr.toLowerCase().includes('not found');
      return (
        <div style={{ padding: '12px 0' }}>
          <p className="ts-empty">Node info unavailable</p>
          <div className="banner danger">
            {is404
              ? `Node "${diagnostics.node_name || nodeName}" not found in the cluster. It may be referenced by IP address.`
              : errStr.slice(0, 200)
            }
          </div>
        </div>
      );
    }

    return (
      <div className="ts-section">
        {hasNodeStruct && (
          <>
            <div className="ts-meta-grid">
              <div><span className="ts-label">Name</span><span className="ts-val">{node.name}</span></div>
              <div><span className="ts-label">OS</span><span className="ts-val">{node.os_image}</span></div>
              <div><span className="ts-label">Kernel</span><span className="ts-val">{node.kernel_version}</span></div>
              <div><span className="ts-label">Kubelet</span><span className="ts-val">{node.kubelet_version}</span></div>
              <div><span className="ts-label">CPU capacity</span><span className="ts-val">{node.capacity?.cpu}</span></div>
              <div><span className="ts-label">Memory capacity</span><span className="ts-val">{node.capacity?.memory}</span></div>
              <div><span className="ts-label">CPU allocatable</span><span className="ts-val">{node.allocatable?.cpu}</span></div>
              <div><span className="ts-label">Memory allocatable</span><span className="ts-val">{node.allocatable?.memory}</span></div>
            </div>
            {node.conditions?.length > 0 && (
              <>
                <h4>Conditions</h4>
                <div className="ts-conditions">
                  {node.conditions.map((c, i) => (
                    <div key={i} className={`ts-condition ${c.type !== 'Ready' && c.status === 'True' ? 'ts-condition-bad' : ''}`}>
                      <span className="ts-label">{c.type}</span>
                      <span className={`ts-badge status-${c.status?.toLowerCase()}`}>{c.status}</span>
                      {c.message && <span className="ts-reason-inline">{c.message}</span>}
                    </div>
                  ))}
                </div>
              </>
            )}
          </>
        )}

        {nodeTop?.length > 0 && (
          <>
            <h4 style={{ marginTop: hasNodeStruct ? 16 : 0 }}>Node Resource Usage (kubectl top node)</h4>
            <pre className="ts-logs" style={{ fontSize: 11 }}>{nodeTop.join('\n')}</pre>
          </>
        )}

        {podsOnNode?.length > 0 && (
          <>
            <h4 style={{ marginTop: 16 }}>Pods on this Node</h4>
            <pre className="ts-logs" style={{ fontSize: 11 }}>{podsOnNode.join('\n')}</pre>
          </>
        )}

        {/* Hide cluster-wide CPU table on a disk alert — irrelevant noise there */}
        {topPodsCpu?.length > 0 && diagnostics?.primary_resource !== 'disk' && (
          <>
            <h4 style={{ marginTop: 16 }}>Top Pods by CPU (cluster-wide)</h4>
            <pre className="ts-logs" style={{ fontSize: 11 }}>{topPodsCpu.join('\n')}</pre>
          </>
        )}

        {/* Top pods by DISK — the "which pod is eating disk" answer (disk alerts) */}
        {diagnostics?.top_pods_by_disk?.length > 0 && (
          <>
            <h4 style={{ marginTop: 16 }}>
              Top Pods by Disk
              {diagnostics?.top_pods_by_disk_node
                ? <span style={{ fontWeight: 'normal', fontSize: 12, color: 'var(--muted)', marginLeft: 6 }}>on {diagnostics.top_pods_by_disk_node}</span>
                : null}
            </h4>
            <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--brand-strong)', marginBottom: 8 }}>
              Per-pod disk from the kubelet. Ephemeral = writable layer + logs + emptyDir on the node root disk (this is what triggers disk-pressure). Local Vol = node-local volumes (emptyDir/hostPath/local PV) — NOT network-backed PVCs. Top row = biggest consumer.
            </div>
            <table className="ts-top-table">
              <thead><tr><th>Namespace</th><th>Pod</th><th>Ephemeral</th><th>Rootfs</th><th>Logs</th><th>Local Vol</th></tr></thead>
              <tbody>
                {diagnostics.top_pods_by_disk.map((p, i) => (
                  <tr key={i} className={i === 0 ? 'ts-top-row-hot' : ''}>
                    <td>{p.namespace}</td>
                    <td className="ts-top-pod">{p.pod}</td>
                    <td className="ts-top-cpu">{p.ephemeral}</td>
                    <td>{p.rootfs}</td>
                    <td>{p.logs}</td>
                    <td>{p.local_volume}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Drill-down: which DIRECTORY inside the top pods eats the space (du) */}
        {diagnostics?.disk_drilldown?.length > 0 && (
          <>
            <h4 style={{ marginTop: 16 }}>Disk breakdown inside top pods (du)</h4>
            <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--brand-strong)', marginBottom: 8 }}>
              Largest directories inside each top consumer — the exact location eating disk. Use this to decide what (if anything) is safe to clear.
            </div>
            {diagnostics.disk_drilldown.map((d, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{d.namespace}/{d.pod}</div>
                <pre className="ts-logs" style={{ fontSize: 11 }}>{(d.du || []).join('\n')}</pre>
                {d.du_deep?.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 2px' }}>
                      Recursive breakdown of <code>{d.biggest_dir}</code> (biggest sub-paths):
                    </div>
                    <pre className="ts-logs" style={{ fontSize: 11 }}>{d.du_deep.join('\n')}</pre>
                  </>
                )}
              </div>
            ))}
          </>
        )}
        {renderCpuMemExtras()}
      </div>
    );
  };

  const renderEvents = () => {
    if (!hasEvents) return <p className="ts-empty">No events</p>;
    return (
      <div className="ts-events">
        {diagnostics.events.map((e, i) => (
          <div key={i} className={`ts-event ts-event-${e.type?.toLowerCase()}`}>
            <span className={`ts-badge event-${e.type?.toLowerCase()}`}>{e.type}</span>
            <span className="ts-event-reason">{e.reason}</span>
            <span className="ts-event-msg">{e.message}</span>
            {e.count > 1 && <span className="ts-event-count">x{e.count}</span>}
          </div>
        ))}
      </div>
    );
  };

  // ── kubectl top: node + pod resource usage ──────────────────────────────────
  const renderTop = () => {
    // node_top_raw or node_top (plain text lines from kubectl top node <name>)
    const _rawCandidate = diagnostics?.node_top_raw || (
      Array.isArray(diagnostics?.node_top) && typeof diagnostics.node_top[0] === 'string'
        ? diagnostics.node_top : null
    );
    // Filter out "metrics-server not available" results
    const nodeTopRaw = _rawCandidate?.some(l => l.toLowerCase().includes('metrics-server') || l.toLowerCase().includes('not available'))
      ? null : _rawCandidate;
    // node_top structured objects from metrics-server API (array of {node,cpu,memory})
    const nodeTopObjs = Array.isArray(diagnostics?.node_top) && typeof diagnostics.node_top[0] === 'object'
      ? diagnostics.node_top : null;
    const podsOnNode    = diagnostics?.pods_on_node;
    const topPodsCpu    = diagnostics?.top_pods_by_cpu;
    const hasAnyData = hasNodeTop || hasPodTop || nodeTopRaw?.length > 0
                       || podsOnNode?.length > 0 || topPodsCpu?.length > 0;

    if (!hasAnyData) {
      return (
        <p className="ts-empty">
          No resource usage data available.
          <br />
          <span style={{ fontSize: 12, color: 'var(--muted)' }}>
            Run diagnostics to collect kubectl top data.
          </span>
        </p>
      );
    }

    return (
      <div className="ts-section">
        {/* Structured node_top from metrics-server */}
        {nodeTopObjs?.length > 0 && (
          <>
            <h4>Node Resource Usage (kubectl top nodes)</h4>
            <table className="ts-top-table">
              <thead><tr><th>Node</th><th>CPU</th><th>Memory</th></tr></thead>
              <tbody>
                {nodeTopObjs.map((n, i) => (
                  <tr key={i}>
                    <td>{n.node}</td>
                    <td className="ts-top-cpu">{n.cpu}</td>
                    <td>{n.memory}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Plain text node_top from kubectl top node <specific-node> */}
        {nodeTopRaw?.length > 0 && (
          <>
            <h4 style={{ marginTop: nodeTopObjs ? 16 : 0 }}>Node Resource Usage (kubectl top node)</h4>
            <pre className="ts-logs" style={{ fontSize: 11 }}>{nodeTopRaw.join('\n')}</pre>
          </>
        )}

        {/* Pods running on this node */}
        {podsOnNode?.length > 0 && (
          <>
            <h4 style={{ marginTop: 16 }}>Pods on Affected Node</h4>
            <pre className="ts-logs" style={{ fontSize: 11 }}>{podsOnNode.join('\n')}</pre>
          </>
        )}

        {/* Top pods by CPU on this node */}
        {topPodsCpu?.length > 0 && (
          <>
            <h4 style={{ marginTop: 16 }}>
              Top Pods by CPU
              {diagnostics?.top_pods_node
                ? <span style={{ fontWeight: 'normal', fontSize: 12, color: 'var(--muted)', marginLeft: 6 }}>on {diagnostics.top_pods_node}</span>
                : null
              }
            </h4>
            <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--brand-strong)', marginBottom: 8 }}>
              CPU in millicores (1000m = 1 core). Cross-reference with node CPU% above to identify which pods are driving the load.
            </div>
            <pre className="ts-logs" style={{ fontSize: 11 }}>{topPodsCpu.join('\n')}</pre>
          </>
        )}

        {/* Top pods by DISK on this node — the "which pod is eating disk" answer */}
        {diagnostics?.top_pods_by_disk?.length > 0 && (
          <>
            <h4 style={{ marginTop: 16 }}>
              Top Pods by Disk
              {diagnostics?.top_pods_by_disk_node
                ? <span style={{ fontWeight: 'normal', fontSize: 12, color: 'var(--muted)', marginLeft: 6 }}>on {diagnostics.top_pods_by_disk_node}</span>
                : null
              }
            </h4>
            <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--brand-strong)', marginBottom: 8 }}>
              Per-pod disk from the kubelet. Ephemeral = writable layer + logs + emptyDir on the node root disk (this is what triggers disk-pressure). Local Vol = node-local volumes (emptyDir/hostPath/local PV) — NOT network-backed PVCs. Top row = biggest consumer.
            </div>
            <table className="ts-top-table">
              <thead><tr><th>Namespace</th><th>Pod</th><th>Ephemeral</th><th>Rootfs</th><th>Logs</th><th>Local Vol</th></tr></thead>
              <tbody>
                {diagnostics.top_pods_by_disk.map((p, i) => (
                  <tr key={i} className={i === 0 ? 'ts-top-row-hot' : ''}>
                    <td>{p.namespace}</td>
                    <td className="ts-top-pod">{p.pod}</td>
                    <td className="ts-top-cpu">{p.ephemeral}</td>
                    <td>{p.rootfs}</td>
                    <td>{p.logs}</td>
                    <td>{p.local_volume}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}

        {/* Drill-down: which DIRECTORY inside the top pods eats the space (du) */}
        {diagnostics?.disk_drilldown?.length > 0 && (
          <>
            <h4 style={{ marginTop: 16 }}>Disk breakdown inside top pods (du)</h4>
            <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '6px 10px', fontSize: 12, color: 'var(--brand-strong)', marginBottom: 8 }}>
              Largest directories inside each top consumer — the exact location eating disk. Use this to decide what (if anything) is safe to clear.
            </div>
            {diagnostics.disk_drilldown.map((d, i) => (
              <div key={i} style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>{d.namespace}/{d.pod}</div>
                <pre className="ts-logs" style={{ fontSize: 11 }}>{(d.du || []).join('\n')}</pre>
                {d.du_deep?.length > 0 && (
                  <>
                    <div style={{ fontSize: 11, color: 'var(--muted)', margin: '6px 0 2px' }}>
                      Recursive breakdown of <code>{d.biggest_dir}</code> (biggest sub-paths):
                    </div>
                    <pre className="ts-logs" style={{ fontSize: 11 }}>{d.du_deep.join('\n')}</pre>
                  </>
                )}
              </div>
            ))}
          </>
        )}

        {/* Structured pod_top from metrics-server */}
        {hasPodTop && (
          <>
            <h4 style={{ marginTop: 16 }}>Top Pods by CPU (kubectl top pods)</h4>
            <table className="ts-top-table">
              <thead><tr><th>Namespace</th><th>Pod</th><th>CPU (m)</th><th>Memory (Mi)</th></tr></thead>
              <tbody>
                {diagnostics.pod_top.map((p, i) => (
                  <tr key={i} className={i === 0 ? 'ts-top-row-hot' : ''}>
                    <td>{p.namespace}</td>
                    <td className="ts-top-pod">{p.pod}</td>
                    <td className="ts-top-cpu">{p.cpu_m}m</td>
                    <td>{p.mem_mi} Mi</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
        {renderCpuMemExtras()}
      </div>
    );
  };

  // ── OS-level diagnostics (SSH/agent) ────────────────────────────────────────
  const renderOsDiag = () => {
    const os = diagnostics?.os_diagnostics;
    if (!os) return <p className="ts-empty">No OS diagnostics available</p>;
    if (os.error) return (
      <div style={{ padding: '12px 0' }}>
        <p className="ts-empty">SSH to node failed</p>
        <div style={{ background: 'var(--color-danger-50)', border: '1px solid var(--color-danger-500)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--color-danger-700)' }}>
          {os.error}
          <br /><br />
          To enable OS diagnostics: add an SSH key to the matching server record or install the SarvKube agent.
        </div>
      </div>
    );

    // Phase 3 format: keys start with "run_commands:" or "fetch_logs:"
    const isPhase3 = Object.keys(os).some(k => k.startsWith('run_commands:') || k.startsWith('fetch_logs:'));

    if (isPhase3) {
      return (
        <div className="ts-section">
          {diagnostics._phase2_requested?.length > 0 && (
            <div style={{ background: 'var(--brand-soft)', border: '1px solid var(--color-brand-200)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--brand-strong)', marginBottom: 12 }}>
              AI requested {diagnostics._phase2_requested.length} diagnostic action{diagnostics._phase2_requested.length !== 1 ? 's' : ''} based on baseline data and playbook hints.
            </div>
          )}
          {Object.entries(os).map(([key, val], i) => {
            if (key.startsWith('run_commands:')) {
              const output = val.output || {};
              const sshError = output._ssh_error;
              const cmdEntries = Object.entries(output).filter(([k]) => k !== '_ssh_error');
              return (
                <div key={i} style={{ marginBottom: 20 }}>
                  <h4 style={{ marginBottom: 4 }}>Commands on <code>{val.server}</code></h4>
                  {val.reason && <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{val.reason}</p>}
                  {sshError && (
                    <div style={{ background: 'var(--color-danger-50)', border: '1px solid var(--color-danger-50)', borderRadius: 4, padding: '6px 10px', fontSize: 12, color: 'var(--color-danger-700)', marginBottom: 8 }}>
                      SSH error: {sshError}
                    </div>
                  )}
                  {cmdEntries.map(([cmd, lines], j) => (
                    <div key={j} style={{ marginBottom: 10 }}>
                      <div style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-2)', background: 'var(--surface-2)', padding: '4px 8px', borderRadius: 4, marginBottom: 4 }}>
                        $ {cmd}
                      </div>
                      <pre className="ts-os-pre">{Array.isArray(lines) ? lines.join('\n') : String(lines || '')}</pre>
                    </div>
                  ))}
                </div>
              );
            }
            if (key.startsWith('fetch_logs:')) {
              const lines = val.lines;
              return (
                <div key={i} style={{ marginBottom: 20 }}>
                  <h4 style={{ marginBottom: 4 }}>Log: <code>{val.path}</code> <span style={{ fontWeight: 'normal', fontSize: 12, color: 'var(--muted)' }}>from {val.server}</span></h4>
                  {val.reason && <p style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 8 }}>{val.reason}</p>}
                  <pre className="ts-os-pre">{Array.isArray(lines) ? lines.join('\n') : String(lines || '')}</pre>
                </div>
              );
            }
            return null;
          })}
        </div>
      );
    }

    // Flat format: keys are either known names (uptime, free_m, ...) or raw command strings.
    // Render all entries generically — command string as header, output as pre block.
    const SKIP_KEYS = new Set(['error', '_ssh_error', '_note']);
    const entries = Object.entries(os).filter(([k]) => !SKIP_KEYS.has(k));

    if (entries.length === 0) {
      return <p className="ts-empty">SSH connected but no OS data returned.</p>;
    }

    return (
      <div className="ts-section">
        {entries.map(([cmd, val], i) => {
          const lines = Array.isArray(val) ? val : (val ? [String(val)] : []);
          // Skip n/a-only results
          if (lines.length === 0 || (lines.length === 1 && lines[0].trim() === 'n/a')) return null;

          // Special inline display for uptime
          if (cmd === 'uptime' || (lines.length === 1 && lines[0].length < 80)) {
            return (
              <div key={i} className="ts-os-row" style={{ marginBottom: 10 }}>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--muted)', marginBottom: 2 }}>$ {cmd}</div>
                <code className="ts-os-val">{lines.join(' ')}</code>
              </div>
            );
          }

          // Warn-style for dmesg/journal
          const isWarn = cmd.includes('dmesg') || cmd.includes('journalctl') || cmd.includes('journal');
          return (
            <div key={i} style={{ marginBottom: 14 }}>
              <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--ink-2)', background: 'var(--surface-2)', padding: '4px 8px', borderRadius: 4, marginBottom: 4 }}>
                $ {cmd}
              </div>
              <pre className={`ts-os-pre${isWarn ? ' ts-os-pre-warn' : ''}`}>{lines.join('\n')}</pre>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="troubleshoot-container">
      <div className="ts-header">
        <div>
          <h3>Diagnostics</h3>
          <p className="ts-subtitle">
            Fetch live logs &amp; pod/node state from the cluster, then analyze with AI.
          </p>
        </div>
        <button
          className={`ts-btn ${loading ? 'ts-btn-loading' : ''}`}
          onClick={runDiagnostics}
          disabled={loading || analyzing}
        >
          {loading ? 'Fetching...' : diagnostics ? 'Re-fetch' : 'Fetch Logs & Diagnostics'}
        </button>
      </div>

      {error && <div className="ts-error-box">{error}</div>}

      {loading && (
        <div className="ts-loading">
          <div className="ts-spinner" />
          {loadingPhase === 'baseline'
            ? <span>Connecting to cluster and fetching live diagnostics...</span>
            : <div>
                <span>Collecting additional diagnostics...</span>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted)' }}>
                  Analyzing baseline data and running playbook-defined commands on node...
                </p>
              </div>
          }
        </div>
      )}

      {diagnostics && !loading && (
        <>
          {/* Stale-snapshot notice: the pod/node this alert names is no longer
              reachable (reaped, rescheduled, cluster unreachable that moment) —
              what's shown below is the last successful capture, not a fresh read. */}
          {diagnostics._stale_snapshot && (
            <div className="banner warning" style={{ marginBottom: 10 }}>
              <strong>Showing last known-good diagnostics.</strong> {diagnostics._stale_reason}
              {diagnostics._latest_fetch_error?.pod?.error && (
                <div style={{ marginTop: 4, fontSize: 12 }}>Latest pod lookup: {diagnostics._latest_fetch_error.pod.error}</div>
              )}
              {diagnostics._latest_fetch_error?.node?.error && (
                <div style={{ marginTop: 4, fontSize: 12 }}>Latest node lookup: {diagnostics._latest_fetch_error.node.error}</div>
              )}
            </div>
          )}
          {/* Cluster badge */}
          <div className="ts-cluster-badge">
            Cluster: <strong>{diagnostics.cluster_used}</strong>
            {diagnostics.pod && <span> · Pod: <strong>{diagnostics.pod.name}</strong></span>}
            {hasLogs && <span> · <strong>{diagnostics.logs.length} log lines</strong> captured</span>}
            {!hasLogs && hasDescribe && <span> · <strong>describe output</strong> captured (no logs — evicted/failed)</span>}
            {hasOsDiag && diagnostics._phase2_requested?.length > 0
              ? <span> · <strong>AI-directed diagnostics</strong> collected ({diagnostics._phase2_requested.length} action{diagnostics._phase2_requested.length !== 1 ? 's' : ''})</span>
              : hasOsDiag
                ? <span> · <strong>OS diagnostics</strong> collected via SSH</span>
                : null
            }
          </div>

          {/* Tabs */}
          <div className="ts-tabs">
            {hasInfo       && <button className={`ts-tab ${activeTab === 'info'     ? 'active' : ''}`} onClick={() => setActiveTab('info')}>ℹ Info</button>}
            {hasOsDiag     && <button className={`ts-tab ${activeTab === 'os'       ? 'active' : ''}`} onClick={() => setActiveTab('os')}>OS / Kernel</button>}
            {(hasNodeTop || hasPodTop || diagnostics?.pods_on_node?.length > 0 || diagnostics?.top_pods_by_cpu?.length > 0) && <button className={`ts-tab ${activeTab === 'top' ? 'active' : ''}`} onClick={() => setActiveTab('top')}>Top (Resources)</button>}
            {hasLogs       && <button className={`ts-tab ${activeTab === 'logs'     ? 'active' : ''}`} onClick={() => setActiveTab('logs')}>Logs ({diagnostics.logs.length})</button>}
            {hasDescribe   && <button className={`ts-tab ${activeTab === 'describe' ? 'active' : ''}`} onClick={() => setActiveTab('describe')} style={{ color: activeTab === 'describe' ? undefined : 'var(--color-warning-500)' }}>Describe {!hasLogs && '⚠'}</button>}
            {hasEvents     && <button className={`ts-tab ${activeTab === 'events'   ? 'active' : ''}`} onClick={() => setActiveTab('events')}>Events ({diagnostics.events.length})</button>}
            {hasPod        && <button className={`ts-tab ${activeTab === 'pod'      ? 'active' : ''}`} onClick={() => setActiveTab('pod')}>Pod</button>}
            {hasNode       && <button className={`ts-tab ${activeTab === 'node'     ? 'active' : ''}`} onClick={() => setActiveTab('node')}>Node</button>}
          </div>

          <div className="ts-content">
            {activeTab === 'info'   && (
              <div className="ts-info-panel">
                <p>{diagnostics.info}</p>
                <p style={{marginTop:8,fontSize:12,color:'var(--muted)'}}>
                  Cluster matched: <strong>{diagnostics.cluster_used}</strong>
                  {diagnostics.match_method && <> · Method: {diagnostics.match_method}</>}
                </p>
              </div>
            )}
            {activeTab === 'os'       && renderOsDiag()}
            {activeTab === 'top'      && renderTop()}
            {activeTab === 'logs'     && renderLogs()}
            {activeTab === 'describe' && renderDescribe()}
            {activeTab === 'events'   && renderEvents()}
            {activeTab === 'pod'      && renderPod()}
            {activeTab === 'node'     && renderNode()}
          </div>

          {/* Analyze with AI — queue immediately, SRE doesn't need to wait */}
          <div className="ts-ai-section">
            {!analyzing ? (
              <>
                <button
                  className="ts-btn-analyze"
                  onClick={analyzeWithAI}
                  disabled={analyzing}
                >
                  Analyze with AI
                  {hasOsDiag
                    ? <span className="ts-ai-badge">OS + kernel data</span>
                    : hasLogs
                      ? <span className="ts-ai-badge">{diagnostics.logs.length} log lines</span>
                      : hasDescribe
                        ? <span className="ts-ai-badge" style={{ background: 'var(--color-warning-50)', color: 'var(--color-warning-700)' }}>describe output</span>
                        : hasEvents
                          ? <span className="ts-ai-badge">{diagnostics.events.length} events</span>
                          : null
                  }
                </button>
                <span className="ts-ai-hint">
                  {hasOsDiag
                    ? 'SARVAI will analyze kernel logs, process list, and memory to diagnose the OS-level issue.'
                    : hasLogs
                      ? 'SARVAI will read the actual logs and give a specific diagnosis — not a generic answer.'
                      : hasDescribe
                        ? 'No container logs (pod evicted/failed). SARVAI will analyze kubectl describe output — eviction reason, node conditions, resource pressure.'
                        : 'SARVAI will analyze pod state, events, and conditions to diagnose the issue.'
                  }
                </span>
              </>
            ) : (
              <div className="ts-loading">
                <div className="ts-spinner" />
                <div>
                  <span>SARVAI is analyzing the data...</span>
                  <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--muted)' }}>
                    This takes 20–30s. Switch to the <strong>AI Analysis</strong> tab — results will appear there when ready.
                  </p>
                </div>
              </div>
            )}
            {analysisError && <div className="ts-error-box" style={{marginTop: 8}}>{analysisError}</div>}
          </div>
        </>
      )}
    </div>
  );
};

export default Troubleshoot;
